import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';

const ROWS_PER_PAGE = 25;

const PaymentList = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/payments/payment-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const formatNumber = (number, decimals = 2) => {
    if (number === null || number === undefined || number === '') return '-';
    return parseFloat(number).toFixed(decimals);
  };
// Using the common formatDateDDMMYY function from dateUtils
  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Payment Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center gap-1 bg-[#2691cc] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#2691cc] font-medium"
            onClick={() => navigate('/payment/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No payment records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm whitespace-nowrap">
            <thead className="bg-gray-100">
              <tr>
                
                <th className="px-3 py-2 text-left font-medium">Purchase Contract No</th>
                <th className="px-3 py-2 text-left font-medium">Payee Type</th>
                <th className="px-3 py-2 text-left font-medium">Payee ID</th>
                <th className="px-3 py-2 text-left font-medium">Party Name</th>
                <th className="px-3 py-2 text-left font-medium">Invoice Date</th>
                <th className="px-3 py-2 text-left font-medium">Invoice No</th>
                <th className="px-3 py-2 text-left font-medium">Amount</th>
                <th className="px-3 py-2 text-left font-medium">Currency</th>
                <th className="px-3 py-2 text-left font-medium">FX Rate</th>
                <th className="px-3 py-2 text-left font-medium">Amount INR Equiv</th>
                <th className="px-3 py-2 text-left font-medium">Payment Date</th>
                <th className="px-3 py-2 text-left font-medium">Collection Ref</th>
                <th className="px-3 py-2 text-left font-medium">SWIFT Code</th>
                <th className="px-3 py-2 text-left font-medium">Presenting Bank</th>
              </tr>
            </thead>
            <tbody>
              {records.map((item, idx) => (
                <tr key={item.payment_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  
                  
                  <td className="px-3 py-1">{item.purchase_contract_no || '-'}</td>
                  <td className="px-3 py-1">{item.payee_type || '-'}</td>
                  <td className="px-3 py-1">{item.payee_id || '-'}</td>
                  <td className="px-3 py-1">{item.party_name || '-'}</td>
                  <td className="px-3 py-1">{formatDateDDMMYY(item.invoice_date)}</td>
                  <td className="px-3 py-1">{item.invoice_no || '-'}</td>
                  <td className="px-3 py-1">{formatNumber(item.amount, 2)}</td>
                  <td className="px-3 py-1">{item.currency || '-'}</td>
                  <td className="px-3 py-1">{formatNumber(item.fx_rate, 4)}</td>
                  <td className="px-3 py-1">{formatNumber(item.amount_inr_equiv, 2)}</td>
                  <td className="px-3 py-1">{formatDateDDMMYY(item.payment_date)}</td>
                  <td className="px-3 py-1">{item.collection_reference || '-'}</td>
                  <td className="px-3 py-1">{item.swift_code || '-'}</td>
                  <td className="px-3 py-1">{item.presenting_bank || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentList;
